import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { marketingManifestApi, asyncTasksListing, asyncTaskResult, asyncTaskCancel } from "@/network/network";
import { showToast } from "@/other/utils/utils";
import { ASYNC_TASK_TYPES, ASYNC_TASK_STATUS } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";

export interface ManifestData {
  id?: number;
  content: string;
  confidence_scores: Record<string, any> | null;
  business_description?: string;
  website_url?: string;
}

export interface HistoryEntry {
  id: number;
  created_at: string;
  [key: string]: any;
}

function normalizeHistoryList(data: unknown): HistoryEntry[] {
  // Handle paginated envelope { count, results } returned by the history list endpoint.
  // Fall back to a direct array for forward-compatibility.
  const list = (data as any)?.results ?? (Array.isArray(data) ? data : []);
  return (list as any[])
    .map((e) => ({ ...e, id: e.history_id, created_at: e.history_date }))
    .filter((e) => Number.isFinite(e.id));
}

export interface GeneratedTag {
  label: string;
  type: string;
  tag_group: string;
  description: string;
}

export interface GeneratedMapping {
  problem_label: string;
  solution_label: string;
}

interface SMMGeneratorContextValue {
  manifest: ManifestData | null;
  editedContent: string;
  confidenceScores: Record<string, any> | null;
  businessDescription: string;
  websiteUrl: string;

  isLoading: boolean;
  isGenerating: boolean;
  isSaving: boolean;
  isScoring: boolean;
  isDirty: boolean;
  scoresStale: boolean;

  history: HistoryEntry[];
  isLoadingHistory: boolean;
  revertingId: number | null;

  previewEntry: HistoryEntry | null;
  previewContent: string | null;
  previewConfidenceScores: Record<string, any> | null;
  isLoadingPreview: boolean;
  handlePreviewHistory: (historyId: number) => Promise<void>;
  clearHistoryPreview: () => void;

  isGeneratingTags: boolean;
  isSavingTags: boolean;
  tagReviewOpen: boolean;
  setTagReviewOpen: (open: boolean) => void;
  generatedTags: GeneratedTag[];
  generatedMappings: GeneratedMapping[];

  hasOpenAIKey: boolean;

  handleGenerate: (desc: string, url: string) => Promise<void>;
  cancelGeneration: () => Promise<void>;
  handleSave: () => Promise<void>;
  handleReScore: () => Promise<void>;
  handleRevert: (historyId: number) => Promise<void>;
  handleContentChange: (content: string) => void;
  handleGenerateTags: () => Promise<void>;
  cancelGenerateTags: () => Promise<void>;
  handleSaveTags: (tags: GeneratedTag[], mappings: GeneratedMapping[]) => Promise<void>;
}

const SMMGeneratorContext = createContext<SMMGeneratorContextValue | undefined>(undefined);

export const SMMGeneratorProvider = ({ children }: { children: ReactNode }) => {
  const { selectedBoard, project, userPreferences, editorHandler } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as any)?.slug as string | undefined;
  const boardId = (selectedBoard?.record as any)?.id as number | undefined;
  const api = useMemo(() => (slug ? marketingManifestApi(slug) : null), [slug]);

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey,
  });

  const hasOpenAIKey = userPreferences
    ? userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true"
    : false;

  const [manifest, setManifest] = useState<ManifestData | null>(null);
  const [editedContent, setEditedContent] = useState("");
  const [confidenceScores, setConfidenceScores] = useState<Record<string, any> | null>(null);
  const [businessDescription, setBusinessDescription] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isScoring, setIsScoring] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);

  const [previewEntry, setPreviewEntry] = useState<HistoryEntry | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewConfidenceScores, setPreviewConfidenceScores] = useState<Record<string, any> | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const savedContentRef = useRef("");
  const scoresStaleRef = useRef(false);
  const [scoresStale, setScoresStale] = useState(false);

  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [tagReviewOpen, setTagReviewOpen] = useState(false);
  const [generatedTags, setGeneratedTags] = useState<GeneratedTag[]>([]);
  const [generatedMappings, setGeneratedMappings] = useState<GeneratedMapping[]>([]);

  // Refs hold the active async task IDs for cancel support.
  // No state is needed — nothing renders based on these values.
  const manifestTaskIdRef = useRef<number | null>(null);
  const tagsTaskIdRef = useRef<number | null>(null);
  // True when the in-flight generation was the very first one (no manifest existed yet).
  // The backend auto-saves in that case, so the poll result should not mark the manifest dirty.
  const isFirstGenerationRef = useRef(false);

  // Task IDs processed in this context session — prevents re-processing the same result
  // on repeated polls after a completed task has been handled.
  const processedTaskIdsRef = useRef<Set<number>>(new Set());

  const loadManifest = useCallback(async (opts?: { silent?: boolean }) => {
    if (!api) return;
    if (!opts?.silent) setIsLoading(true);
    try {
      const response = await api.get();
      const data = response.data;
      // The API now returns 200 + { detail: "..." } when no manifest exists yet
      // (previously it returned 404). Treat a detail-only body as "no manifest".
      if (data?.detail != null || data?.content == null) {
        return;
      }
      setManifest(data);
      setEditedContent(data.content || "");
      savedContentRef.current = data.content || "";
      setConfidenceScores(data.confidence_scores || null);
      setBusinessDescription(data.business_description || "");
      setWebsiteUrl(data.website_url || "");
      setIsDirty(false);
      scoresStaleRef.current = false;
      setScoresStale(false);
    } catch {
      // Errors (network, 500, etc.) are swallowed; manifest stays null / previous value.
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const loadHistory = useCallback(async () => {
    if (!api) return;
    setIsLoadingHistory(true);
    try {
      const response = await api.getHistory();
      setHistory(normalizeHistoryList(response.data));
    } catch {
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [api]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  useEffect(() => {
    if (manifest) {
      loadHistory();
    }
  }, [manifest, loadHistory]);

  // Async task polling — mirrors the pattern used in MySwivuPage.
  // Runs immediately on mount then every 10 seconds.
  // Responsible for:
  //   - Detecting pending tasks (resume after page reload)
  //   - Fetching results when tasks complete
  //   - Clearing loading state on cancellation or failure
  useEffect(() => {
    if (!api || !boardId) return;

    let isMounted = true;

    const pollAsyncTasks = async () => {
      try {
        const response = await asyncTasksListing();
        if (!isMounted) return;

        const allTasks: any[] = response.data || [];
        const boardTasks = allTasks
          .filter((task: any) => task.meta_data?.board_id === boardId)
          .sort((a: any, b: any) => b.id - a.id);

        const manifestTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION && !task.is_retrieved,
        );
        const tagsTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION && !task.is_retrieved,
        );

        // --- Manifest task handling ---
        if (manifestTask) {
          if (manifestTask.status === ASYNC_TASK_STATUS.PENDING) {
            manifestTaskIdRef.current = manifestTask.id;
            setIsGenerating(true);
          } else if (
            manifestTask.status === ASYNC_TASK_STATUS.COMPLETED &&
            !processedTaskIdsRef.current.has(manifestTask.id)
          ) {
            processedTaskIdsRef.current.add(manifestTask.id);
            manifestTaskIdRef.current = null;
            const wasFirstGeneration = isFirstGenerationRef.current;
            isFirstGenerationRef.current = false;
            try {
              if (wasFirstGeneration) {
                // Backend auto-saved the result — reload cleanly so isDirty stays false.
                await loadManifest({ silent: true });
                if (isMounted) loadHistory();
              } else {
                const resultResponse = await asyncTaskResult(manifestTask.id);
                if (!isMounted) return;
                const data = resultResponse.data;
                setManifest(data);
                setEditedContent(data.content || "");
                savedContentRef.current = data.content || "";
                setConfidenceScores(data.confidence_scores || null);
                setBusinessDescription(data.business_description || "");
                setWebsiteUrl(data.website_url || "");
                setIsDirty(true);
                scoresStaleRef.current = true;
                setScoresStale(true);
                loadHistory();
              }
            } catch {
              showToast({ msg: "Failed to retrieve manifest generation result.", type: "error", duration: 3000 });
            } finally {
              if (isMounted) setIsGenerating(false);
            }
          } else if (
            manifestTask.status === ASYNC_TASK_STATUS.CANCELLED ||
            manifestTask.status === ASYNC_TASK_STATUS.FAILED
          ) {
            if (manifestTaskIdRef.current === manifestTask.id) {
              manifestTaskIdRef.current = null;
              setIsGenerating(false);
              if (manifestTask.status === ASYNC_TASK_STATUS.FAILED) {
                showToast({ msg: "Manifest generation failed.", type: "error", duration: 3000 });
              }
            }
          }
        }

        // --- Tags task handling ---
        if (tagsTask) {
          if (tagsTask.status === ASYNC_TASK_STATUS.PENDING) {
            tagsTaskIdRef.current = tagsTask.id;
            setIsGeneratingTags(true);
          } else if (
            tagsTask.status === ASYNC_TASK_STATUS.COMPLETED &&
            !processedTaskIdsRef.current.has(tagsTask.id)
          ) {
            processedTaskIdsRef.current.add(tagsTask.id);
            tagsTaskIdRef.current = null;
            try {
              const resultResponse = await asyncTaskResult(tagsTask.id);
              if (!isMounted) return;
              const data = resultResponse.data;
              setGeneratedTags(data.tags || []);
              setGeneratedMappings(data.mappings || []);
              setTagReviewOpen(true);
            } catch {
              showToast({ msg: "Failed to retrieve tags generation result.", type: "error", duration: 3000 });
            } finally {
              if (isMounted) setIsGeneratingTags(false);
            }
          } else if (
            tagsTask.status === ASYNC_TASK_STATUS.CANCELLED ||
            tagsTask.status === ASYNC_TASK_STATUS.FAILED
          ) {
            if (tagsTaskIdRef.current === tagsTask.id) {
              tagsTaskIdRef.current = null;
              setIsGeneratingTags(false);
              if (tagsTask.status === ASYNC_TASK_STATUS.FAILED) {
                showToast({ msg: "Tags generation failed.", type: "error", duration: 3000 });
              }
            }
          }
        }
      } catch {
        // Silently ignore polling errors to avoid disrupting the UI
      }
    };

    pollAsyncTasks();
    const intervalId = setInterval(pollAsyncTasks, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [api, boardId, loadHistory]);

  const handlePreviewHistory = useCallback(async (historyId: number) => {
    if (!api) return;
    setIsLoadingPreview(true);
    try {
      const response = await api.getHistoryEntry(historyId);
      const data = response.data;
      const entry = history.find((e) => e.id === historyId) ?? null;
      setPreviewEntry(entry);
      setPreviewContent(data.content ?? "");
      setPreviewConfidenceScores(data.confidence_scores ?? null);
    } catch {
      showToast({ msg: "Failed to load version preview.", type: "error", duration: 3000 });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [api, history]);

  const clearHistoryPreview = useCallback(() => {
    setPreviewEntry(null);
    setPreviewContent(null);
    setPreviewConfidenceScores(null);
  }, []);

  const handleGenerate = useCallback(
    async (desc: string, url: string) => {
      if (!api) return;
      // Record whether this is the first generation so the poll handler can skip
      // setting isDirty when the backend has already auto-saved the result.
      isFirstGenerationRef.current = manifest === null;
      setIsGenerating(true);
      setBusinessDescription(desc);
      setWebsiteUrl(url);
      try {
        const response = await api.generate({ business_description: desc, website_url: url });
        manifestTaskIdRef.current = response.data.id;
        // isGenerating stays true — polling will clear it when the task completes
        if (shouldShowAsyncDialog("MARKETING_MANIFEST_GENERATION")) {
          (editorHandler as any).openEditor(
            EDITOR_KEYS.asyncTaskMessageEditor,
            null,
            { record: "MARKETING_MANIFEST_GENERATION" },
            { useExisting: true, by: "recordType" },
          );
        }
      } catch {
        showToast({ msg: "Failed to start manifest generation. Please try again.", type: "error", duration: 4000 });
        manifestTaskIdRef.current = null;
        isFirstGenerationRef.current = false;
        setIsGenerating(false);
      }
    },
    [api, editorHandler, manifest],
  );

  const cancelGeneration = useCallback(async () => {
    const taskId = manifestTaskIdRef.current;
    if (!taskId) return;
    try {
      await asyncTaskCancel(taskId);
    } catch {
      // Even if cancel API fails, clear local loading state
    } finally {
      manifestTaskIdRef.current = null;
      setIsGenerating(false);
      loadManifest({ silent: true });
      loadHistory();
    }
  }, [loadManifest, loadHistory]);

  const handleSave = useCallback(async () => {
    if (!api) return;
    setIsSaving(true);
    try {
      let scoresToSave = confidenceScores;

      if (scoresStaleRef.current) {
        setIsScoring(true);
        try {
          const scoreResponse = await api.score(editedContent);
          scoresToSave = scoreResponse.data;
          setConfidenceScores(scoresToSave);
          scoresStaleRef.current = false;
          setScoresStale(false);
        } finally {
          setIsScoring(false);
        }
      }

      const response = await api.save({
        content: editedContent,
        confidence_scores: scoresToSave,
        business_description: businessDescription,
        website_url: websiteUrl,
      });
      const data = response.data;
      setManifest(data);
      const savedContent = data.content || editedContent;
      setEditedContent(savedContent);
      savedContentRef.current = savedContent;
      if (data.confidence_scores) setConfidenceScores(data.confidence_scores);
      setIsDirty(false);
      showToast({ msg: "Manifest saved successfully.", type: "success", duration: 2000 });
      loadHistory();
    } catch {
      showToast({ msg: "Failed to save manifest.", type: "error", duration: 3000 });
    } finally {
      setIsSaving(false);
    }
  }, [api, editedContent, confidenceScores, businessDescription, websiteUrl, loadHistory]);

  const handleReScore = useCallback(async () => {
    if (!api) return;
    setIsScoring(true);
    try {
      const response = await api.score(editedContent);
      setConfidenceScores(response.data);
      scoresStaleRef.current = false;
      setScoresStale(false);
      showToast({ msg: "Confidence scores updated.", type: "success", duration: 2000 });
    } catch {
      showToast({ msg: "Failed to re-evaluate scores.", type: "error", duration: 3000 });
    } finally {
      setIsScoring(false);
    }
  }, [api, editedContent]);

  const handleRevert = useCallback(
    async (historyId: number) => {
      if (!api) return;
      setRevertingId(historyId);
      try {
        await api.revert(historyId);
        await loadManifest({ silent: true });
        await loadHistory();
        showToast({ msg: "Manifest reverted successfully.", type: "success", duration: 2000 });
      } catch {
        showToast({ msg: "Failed to revert manifest.", type: "error", duration: 3000 });
      } finally {
        setRevertingId(null);
      }
    },
    [api, loadManifest, loadHistory],
  );

  const handleContentChange = useCallback((content: string) => {
    setEditedContent(content);
    const dirty = content !== savedContentRef.current;
    setIsDirty(dirty);
    if (dirty) {
      scoresStaleRef.current = true;
      setScoresStale(true);
    }
  }, []);

  const handleGenerateTags = useCallback(async () => {
    if (!api) return;
    setIsGeneratingTags(true);
    try {
      const response = await api.generateTags();
      tagsTaskIdRef.current = response.data.id;
      // isGeneratingTags stays true — polling will clear it when the task completes
      if (shouldShowAsyncDialog("MARKETING_TAGS_GENERATION")) {
        (editorHandler as any).openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "MARKETING_TAGS_GENERATION" },
          { useExisting: true, by: "recordType" },
        );
      }
    } catch {
      showToast({ msg: "Failed to start tags generation.", type: "error", duration: 3000 });
      tagsTaskIdRef.current = null;
      setIsGeneratingTags(false);
    }
  }, [api, editorHandler]);

  const cancelGenerateTags = useCallback(async () => {
    const taskId = tagsTaskIdRef.current;
    if (!taskId) return;
    try {
      await asyncTaskCancel(taskId);
    } catch {
      // Even if cancel API fails, clear local loading state
    } finally {
      tagsTaskIdRef.current = null;
      setIsGeneratingTags(false);
    }
  }, []);

  const handleSaveTags = useCallback(
    async (tags: GeneratedTag[], mappings: GeneratedMapping[]) => {
      if (!project) return;
      setIsSavingTags(true);
      try {
        const tagStore = project.getCrudStore(STORE_KEYS.marketingTagStore);
        const mapStore = project.getCrudStore(STORE_KEYS.problemSolutionTagMapStore);
        const currentBoardId = selectedBoard?.record?.id;

        if (!tagStore || !currentBoardId) {
          showToast({ msg: "Unable to access tag store.", type: "error", duration: 3000 });
          return;
        }

        const newRecords = tags.map((t) => ({
          board: currentBoardId,
          label: t.label,
          type: t.type,
          tag_group: t.tag_group,
          description: t.description,
        }));

        tagStore.add(newRecords);
        await project.sync();

        if (mapStore && mappings.length > 0) {
          const allTags = tagStore.records as any[];
          const labelToId = new Map<string, number>();
          for (const rec of allTags) {
            labelToId.set(rec.label, rec.id);
          }

          const mapRecords = mappings
            .map((m) => {
              const problemId = labelToId.get(m.problem_label);
              const solutionId = labelToId.get(m.solution_label);
              if (!problemId || !solutionId) return null;
              return { board: currentBoardId, problem_tag: problemId, solution_tag: solutionId };
            })
            .filter(Boolean);

          if (mapRecords.length > 0) {
            mapStore.add(mapRecords);
            await project.sync();
          }
        }

        setTagReviewOpen(false);
        showToast({ msg: `${tags.length} tags created successfully.`, type: "success", duration: 3000 });
      } catch {
        showToast({ msg: "Failed to save tags.", type: "error", duration: 3000 });
      } finally {
        setIsSavingTags(false);
      }
    },
    [project, selectedBoard],
  );

  const value: SMMGeneratorContextValue = {
    manifest,
    editedContent,
    confidenceScores,
    businessDescription,
    websiteUrl,
    isLoading,
    isGenerating,
    isSaving,
    isScoring,
    isDirty,
    scoresStale,
    history,
    isLoadingHistory,
    revertingId,
    previewEntry,
    previewContent,
    previewConfidenceScores,
    isLoadingPreview,
    handlePreviewHistory,
    clearHistoryPreview,
    isGeneratingTags,
    isSavingTags,
    tagReviewOpen,
    setTagReviewOpen,
    generatedTags,
    generatedMappings,
    hasOpenAIKey,
    handleGenerate,
    cancelGeneration,
    handleSave,
    handleReScore,
    handleRevert,
    handleContentChange,
    handleGenerateTags,
    cancelGenerateTags,
    handleSaveTags,
  };

  return <SMMGeneratorContext.Provider value={value}>{children}</SMMGeneratorContext.Provider>;
};

export const useSMMGeneratorContext = () => {
  const context = useContext(SMMGeneratorContext);
  if (context === undefined) {
    throw new Error("useSMMGeneratorContext must be used within a SMMGeneratorProvider");
  }
  return context;
};
